import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import LandingLayout from '../components/LandingLayout';

export default function AboutPage() {
  const { t } = useTranslation();

  return (
    <LandingLayout>
      <div className="min-h-screen bg-dark-950">
        {/* Hero Section */}
        <section className="py-20 bg-gradient-to-br from-primary-600/20 via-secondary-600/20 to-dark-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-5xl md:text-6xl font-extrabold mb-6 text-white">
              {t('legal.about.hero.title')}
            </h1>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              {t('legal.about.hero.subtitle')}
            </p>
          </div>
        </section>

        {/* Mission Section */}
        <section className="py-20 bg-dark-900">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-extrabold text-white mb-8 text-center">
              {t('legal.about.mission.title')}
            </h2>
            <p className="text-xl text-gray-300 text-center leading-relaxed">
              {t('legal.about.mission.content')}
            </p>
          </div>
        </section>

        {/* Problem Section */}
        <section className="py-20 bg-dark-950">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-extrabold text-white mb-12 text-center">
              {t('legal.about.problem.title')}
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-8">
                <h3 className="text-2xl font-bold text-red-400 mb-4">
                  {t('legal.about.problem.before.title')}
                </h3>
                <ul className="space-y-4 text-gray-300">
                  <li className="flex items-start gap-3">
                    <span className="text-red-400 text-xl">✗</span>
                    <span>{t('legal.about.problem.before.item1')}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-red-400 text-xl">✗</span>
                    <span>{t('legal.about.problem.before.item2')}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-red-400 text-xl">✗</span>
                    <span>{t('legal.about.problem.before.item3')}</span>
                  </li>
                </ul>
              </div>
              <div className="bg-green-500/10 border border-green-500/30 rounded-2xl p-8">
                <h3 className="text-2xl font-bold text-green-400 mb-4">
                  {t('legal.about.problem.after.title')}
                </h3>
                <ul className="space-y-4 text-gray-300">
                  <li className="flex items-start gap-3">
                    <span className="text-green-400 text-xl">✓</span>
                    <span>{t('legal.about.problem.after.item1')}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-green-400 text-xl">✓</span>
                    <span>{t('legal.about.problem.after.item2')}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-green-400 text-xl">✓</span>
                    <span>{t('legal.about.problem.after.item3')}</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Technology Section */}
        <section className="py-20 bg-dark-900">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-extrabold text-white mb-8 text-center">
              {t('legal.about.technology.title')}
            </h2>
            <p className="text-lg text-gray-300 text-center leading-relaxed mb-12">
              {t('legal.about.technology.content')}
            </p>
            <div className="grid sm:grid-cols-3 gap-6">
              <div className="text-center p-6 bg-white/5 rounded-2xl border border-white/10">
                <div className="text-5xl mb-4">🤖</div>
                <h3 className="text-lg font-bold text-white mb-2">
                  {t('legal.about.technology.ai.title')}
                </h3>
                <p className="text-gray-400 text-sm">
                  {t('legal.about.technology.ai.description')}
                </p>
              </div>
              <div className="text-center p-6 bg-white/5 rounded-2xl border border-white/10">
                <div className="text-5xl mb-4">📜</div>
                <h3 className="text-lg font-bold text-white mb-2">
                  {t('legal.about.technology.license.title')}
                </h3>
                <p className="text-gray-400 text-sm">
                  {t('legal.about.technology.license.description')}
                </p>
              </div>
              <div className="text-center p-6 bg-white/5 rounded-2xl border border-white/10">
                <div className="text-5xl mb-4">📻</div>
                <h3 className="text-lg font-bold text-white mb-2">
                  {t('legal.about.technology.radio.title')}
                </h3>
                <p className="text-gray-400 text-sm">
                  {t('legal.about.technology.radio.description')}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Company Info */}
        <section className="py-20 bg-dark-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-extrabold text-white mb-8">
              {t('legal.about.company.title')}
            </h2>
            <div className="bg-white/5 rounded-2xl border border-white/10 p-8">
              <p className="text-gray-300 mb-4">
                {t('legal.about.company.name')}
              </p>
              <p className="text-gray-400 mb-2">
                {t('legal.about.company.address')}
              </p>
              <p className="text-primary-400 mt-6">
                {t('legal.contact.email')}
              </p>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-br from-primary-600/20 via-secondary-600/20 to-dark-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-extrabold text-white mb-6">
              {t('legal.about.cta.title')}
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              {t('legal.about.cta.subtitle')}
            </p>
            <Link
              to="/register"
              className="inline-block px-10 py-5 text-lg font-bold text-white bg-gradient-to-r from-orange-600 to-orange-500 hover:from-orange-500 hover:to-orange-400 rounded-xl transition-all shadow-2xl hover:shadow-orange-500/50 hover:scale-105"
            >
              {t('legal.about.cta.button')}
            </Link>
          </div>
        </section>
      </div>
    </LandingLayout>
  );
}
